import React, {Fragment} from 'react';
import {func, object} from 'prop-types';
import {FormattedMessage, injectIntl} from 'react-intl';
import {Button, Container, Divider, Grid, Header, Icon} from 'semantic-ui-react';
import BreakSummary from 'components/common/BreakSummary';
import PlatformColumn from './PlatformColumn';
import SortableStepTypes from './SortableStepTypes';
import PublishBreakModalContainer from 'pages/BreakPublish/PublishBreakModalContainer';
import ValidateBreakModalContainer from 'pages/BreakPublish/ValidateBreakModalContainer';
import map from 'lodash/map';
import get from 'lodash/get';
import has from 'lodash/has';
import omit from 'lodash/omit';
import partial from 'lodash/partial';
import isEmpty from 'lodash/isEmpty';
import {breakUrl, clone, formatDate, platformMappings} from 'utils/DBUtils';
import UnpublishBreakModalContainer from "../BreakPublish/UnpublishBreakModalContainer";
import {isNoU} from "../../utils/DBUtils";
import VerifyUnpublishChallengeModal from "../BreakPublish/VerifyUnpublishChallengeModal";
import Config from 'Config';

class ViewAllPlatformsPage extends React.Component {
  static propTypes = {
    break: object,
    openAddStepsModal: func.isRequired,
    openDeleteStepsModal: func.isRequired,
    togglePublishStep: func.isRequired,
    syncMaster: func.isRequired,
    saveStepNumbers: func.isRequired
  };

  static defaultProps = {
    break: {},
    platformsBeingUnpublished: [],
      platformsBeingPublished: [],
      platformsBeingValidated: []
  };

  state = {
    sortable: false,
    steps: {},
    lastPlatformModified: null
  };

  constructor(props) {
    super(props);

    // called in other contexts.
    this.togglePublishStep = this.togglePublishStep.bind(this);
    this.toggleSortability = this.toggleSortability.bind(this);
    this.findStep = this.findStep.bind(this);
    this.moveStep = this.moveStep.bind(this);
  }

  componentDidMount() {
    this.loadSteps(this.props.break);
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.savingStepOrder) {
      this.loadSteps(nextProps.break);
    }
  }

  loadSteps = challenge => {
    const masterSteps = get(challenge, 'MASTER.steps', []);
    const breaks = omit(challenge, 'MASTER');

    const steps = Object.keys(platformMappings).reduce((nextSteps, platformKey) => {
      const brk = get(breaks, platformKey, {});
      const synchronized = !isEmpty(brk);

      let unlabeledSteps = {};

      const platformSteps = (synchronized ? get(brk, 'steps', []) : masterSteps)
          .map(step => ({...step, synchronized}))
          .map(step => {
            const masterStep = masterSteps.find(masterStep => masterStep.id === step.id);
            // any step deleted on master cannot exists on an individual platform
            if (isNoU(masterStep)) {
              return null;
            }

            if (step.label) {
              return step;
            } else {
              const masterStepHasLabel = get(masterStep, 'label', false);

              if (masterStepHasLabel) {
                // FIXME: this is a workaround for an API bug where platform step labels do not
                //        currently inherit from their corresponding master step labels.
                return {
                  ...step,
                  label: masterStep.label
                };
              } else {
                const { type } = step;
                unlabeledSteps[type] = unlabeledSteps[type] ? (unlabeledSteps[type] + 1) : 1;

                // if the user hasn't labeled this step and it's not the first of its type
                // (on this platform), appened its instance number to step.type and pass
                // that down as the label.
                const labelIndex = unlabeledSteps[type] > 1 ? unlabeledSteps[type] : '';
                const id = `step_types.${type.toLowerCase()}`;
                const label = [this.props.intl.formatMessage({ id }), labelIndex].join(' ');

                return {...step, label};
              }
            }
          });

      const filtered = platformSteps.filter(step => step !== null);
      filtered.sort((step1, step2) => step1.displayOrder - step2.displayOrder);

      nextSteps[platformKey] = filtered;
      return nextSteps;
    }, {});

    this.setState({ steps });
  };

  get hasEnoughStepsToSort () {
    const stepsPerPlatform = Object.keys(this.state.steps).map(key => ({
      key,
      total: this.state.steps[key].length
    }));

    return stepsPerPlatform.some(({ total }) => total > 1);
  }

  togglePublishStep(platformKey, stepResourceId, publishStep) {
    const brk = this.props.break[platformKey];
    const master = this.props.break["MASTER"];
    const breakResourceId = brk.resourceId;

    // Resolve index through master because this will also happen when saving
    const stepIndex = master.steps.findIndex((step) => step.id === stepResourceId.id);
    this.props.togglePublishStep(stepResourceId, publishStep, breakResourceId, stepIndex);
  }

  toggleSortability(event, data) {
    this.setState({sortable: data.checked});
  }

  findStep(platformKey, stepId) {
    const steps = this.state.steps[platformKey];
    const step = steps.find(s => s.id === stepId);
    return {
      step,
      index: steps.indexOf(step)
    };
  }

  moveStep(platformKey, stepId, atIndex) {
    const master = get(this.props.break, 'MASTER', {});
    let masterSteps = master.steps;

    const nextPlatformSteps = clone(this.state.steps[platformKey]);
    const { step, index } = this.findStep(platformKey, stepId);

    nextPlatformSteps.splice(index, 1);
    nextPlatformSteps.splice(atIndex, 0, step);

    const breakResourceId = this.props.break[platformKey].resourceId;
    const stepNumbersAndResourceIds = nextPlatformSteps
        .filter(step => !step.deleted)
        .map((step, index) => step.displayOrder || (masterSteps.find((mStep) => mStep.id === step.id).displayOrder));

    this.props.saveStepNumbers(breakResourceId, stepNumbersAndResourceIds);
  }

  render() {
    const master = get(this.props.break, 'MASTER', {});
    const dailybreak = get(this.props.break, 'DAILYBREAK', null);
    let masterSteps = master.steps;

    let points = master.possibleCoinsAwarded;
    if (isNoU(points) || isNoU(dailybreak)) {
      points = "N/A";
    }

    return (
      <Container text>
        {!this.props.break ? (
          <div className="loading-spinner fadeInDown animResponse">
            <Icon loading name="spinner" size="huge" />
            <p><FormattedMessage id="loading.break" /></p>
          </div>
        ) : (
          <div className="view-all-page">
            <BreakSummary
              header={master.permalink || ''}
              breakType={master.sponsoredType || 'N/A'}
              title={master.title}
              permalink={master.permalink}
              lang={master.lang}
              challengeId={master.challengeId}
              userCanPublish={this.props.userCanPublishBreaks}
              userCanUnpublish={this.props.userCanUnpublishBreaks}
              userCanViewAnalytics={this.props.userCanViewAnalytics}
              showViewAllLink={false}
              showMasterLink={true}
              showCopyBreakModal={() => this.props.showCopyBreakModal(master.id, master.permalink)}
              site={master.lang || 'N/A'}
              points={points}
              featured={this.props.break ? (master.featured ? 'Yes' : 'No') : 'N/A'}
              publishChallenge={ () => this.props.publishChallenge(master.challengeId, this.props.break ? Object.keys(this.props.break) : [], this.props.break || {}) }
              challengeIsPublishing={this.props.challengeIsPublishing && (this.props.platformsBeingPublished != null && (this.props.platformsBeingPublished.length > 1 || Object.keys(this.props.break).length === 1))}
              validateChallenge={ () => this.props.validateChallenge(master, this.props.break ? Object.keys(this.props.break) : []) }
              challengeIsValidating={this.props.challengeIsValidating && (this.props.platformsBeingValidated != null && (this.props.platformsBeingValidated.length > 1 || Object.keys(this.props.break).length === 1))} />
            {this.state.steps && (
              <div>
                <Divider horizontal className="lt-grey-rule" />
                <Grid columns={Object.keys(platformMappings).length} className="db-platform-steps">
                  {map(platformMappings, (platformName, platformKey) => (
                    <Grid.Column key={platformName}>
                      <Container text textAlign="center">
                        <Header
                          as="h3"
                          className={`db-platform-name db-platform-name-${platformName.toLowerCase()}`}>
                          {platformName}
                        </Header>
                      </Container>
                      <PlatformColumn
                        steps={get(this.state.steps, platformKey, [])}
                        masterSteps={masterSteps}
                        challengeId={master.challengeId}
                        itemType={SortableStepTypes[platformKey]}
                        sortable={this.state.sortable}
                        platformKey={platformKey}
                        platformName={platformName}
                        togglePublishStep={partial(this.togglePublishStep, platformKey)}
                        findStep={this.findStep}
                        moveStep={this.moveStep}/>
                        {has(this.props.break, platformKey) ? (
                            <Grid className='view-all-platform-actions'>
                              <Grid.Row columns={3}>
                                <Grid.Column key="add-steps">
                                  <Button
                                      onClick={partial(this.props.openAddStepsModal, platformKey)}
                                      className="db-button-add-step">
                                    <FormattedMessage id="editbreak.view_all.button.add"/>
                                  </Button>
                                </Grid.Column>
                                <Grid.Column key="modified">
                                  {this.props.break[platformKey].publishStatus === "DRAFT" ? <Fragment><FormattedMessage id="modified"/> {formatDate(this.props.break[platformKey]._modified, 'MM/DD/YYYY')}</Fragment>: ""}
                                </Grid.Column>
                                  <Grid.Column key={platformKey !== "DAILYBREAK" ? "preview": "publish-platform =="}>
                                    {platformKey !== "DAILYBREAK" ?
                                      <a href={Config.ENV_PREVIEW_SITE_PREFIX[this.props.break[platformKey].lang ? this.props.break[platformKey].lang : 'en-US'] + breakUrl(this.props.break[platformKey].permalink,platformKey)} target="_blank">
                                        <Button primary>
                                          <FormattedMessage id="buttons.preview"/>
                                        </Button>
                                      </a>
                                    : this.props.userCanPublishBreaks && (
                                       <Button
                                         onClick={() => this.props.publishChallenge(master.challengeId, [platformKey], this.props.break[platformKey])}
                                         className={`db-button-publish-platform primary${this.props.break[platformKey].publishStatus === "DRAFT" ? ' outline' : ''}`}
                                         loading={this.props.challengeIsPublishing && this.props.platformsBeingPublished.length === 1 && this.props.platformsBeingPublished[0] === platformKey}>
                                         <FormattedMessage id="buttons.publish"/>
                                       </Button>
                                     )}

                                  </Grid.Column>
                              </Grid.Row>
                              <Grid.Row columns={2}>
                                <Grid.Column key="delete-steps">
                                    {this.props.break[platformKey].publishStatus === "DRAFT" ?
                                        <Button
                                            onClick={partial(this.props.openDeleteStepsModal, platformKey)}
                                            className="db-button-delete-step">
                                          <FormattedMessage id="editbreak.view_all.button.delete"/>
                                        </Button> : ""}
                                </Grid.Column>
                                  <Grid.Column key={platformKey !== "DAILYBREAK" ? "unpublish-platform" : "publish-platform =="}>
                                    {platformKey !== "DAILYBREAK" ?

                                        <Button
                                            onClick={() => this.props.publishChallenge(master.challengeId, [platformKey], this.props.break[platformKey])}
                                            className={`db-button-publish-platform primary${this.props.break[platformKey].publishStatus === "DRAFT" ? ' outline' : ''}`}
                                            loading={this.props.challengeIsPublishing && this.props.platformsBeingPublished.length === 1 && this.props.platformsBeingPublished[0] === platformKey}>
                                            <FormattedMessage id="buttons.publish"/>
                                        </Button>
                                    :
                                    !isNoU(this.props.break[platformKey].lastPublishedDate) ? (
                                        <Button
                                            onClick={() => this.props.unpublishChallenge(master.challengeId, [platformKey], this.props.break[platformKey])}
                                            className={`db-button-publish-unpublish primary${this.props.break[platformKey].publishStatus !== "DRAFT" ? ' outline' : ''}`}
                                            loading={this.props.challengeIsPublishing && this.props.platformsBeingUnpublished.length === 1 && this.props.platformsBeingUnpublished[0] === platformKey}>
                                            <FormattedMessage id="buttons.unpublish"/>
                                        </Button>
                                    ) : ""
                                    }


                                  </Grid.Column>
                              </Grid.Row>
                              <Grid.Row columns={2}>
                                <Grid.Column>

                                </Grid.Column>
                                <Grid.Column key={platformKey !== "DAILYBREAK" ? "unpublish-platform" : ""}>
                                  {this.props.userCanPublishBreaks && this.props.userCanUnpublishBreaks && !isNoU(this.props.break[platformKey].lastPublishedDate) && platformKey !== "DAILYBREAK" ? (
                                      <Button
                                          onClick={() => this.props.unpublishChallenge(master.challengeId, [platformKey], this.props.break[platformKey])}
                                          className={`db-button-publish-unpublish primary${this.props.break[platformKey].publishStatus !== "DRAFT" ? ' outline' : ''}`}
                                          loading={this.props.challengeIsPublishing && this.props.platformsBeingUnpublished.length === 1 && this.props.platformsBeingUnpublished[0] === platformKey}>
                                          <FormattedMessage id="buttons.unpublish"/>
                                      </Button>
                                  ) : ""}
                                </Grid.Column>
                              </Grid.Row>
                            </Grid>
                        ) : (
                            <Grid.Row>
                          <Button
                            onClick={partial(this.props.syncMaster, platformKey)}
                            className="db-button-sync-master">
                            <FormattedMessage id="editbreak.view_all.button.build" /> {platformName}
                          </Button>
                        </Grid.Row>
                      )}
                    </Grid.Column>
                  ))}
                </Grid>
              </div>
            )}
            <PublishBreakModalContainer />
            <UnpublishBreakModalContainer />
            <ValidateBreakModalContainer />
            <VerifyUnpublishChallengeModal />
          </div>
        )}
      </Container>
    );
  }
}

export default injectIntl(ViewAllPlatformsPage);



// WEBPACK FOOTER //
// ./public/pages/ViewAllPlatforms/ViewAllPlatformsPage.jsx